import { supabase } from "@/integrations/supabase/client";
import { nativePlatform } from "@/lib/store-billing";

/**
 * Social sign-in for the native iOS/Android wrappers.
 *
 * Why this exists: the managed broker helper navigates to the *relative* path
 * `/~oauth/initiate`, which only exists on the hosted site. Inside a Capacitor
 * wrapper the page is served from the bundled `dist/` on `localhost` /
 * `capacitor://localhost`, so that navigation resolves to a file that isn't in
 * the bundle and the webview renders a 404 — the failure users reported for
 * "Continue with Google" and (on Android) "Continue with Apple".
 *
 * The native flow instead:
 *  1. asks Supabase for the provider authorization URL (PKCE, verifier stays in
 *     the app webview),
 *  2. opens it in the system browser (real Google/Apple UI, not a webview —
 *     which Google requires),
 *  3. the provider returns to `https://<site>/auth/callback?native=1`, a real
 *     public route that immediately bounces to the app's custom scheme,
 *  4. the app receives the deep link, exchanges the code for a session.
 *
 * Web behaviour is untouched.
 */

/** Public site that owns the OAuth callback route (must be allow-listed). */
export const OAUTH_SITE_URL = "https://www.inquira.online";

/** Custom scheme the native shells register (matches Capacitor appId). */
export const NATIVE_OAUTH_SCHEME = "online.inquira.app";
export const NATIVE_OAUTH_CALLBACK = `${NATIVE_OAUTH_SCHEME}://auth-callback`;

export type NativeOAuthProvider = "google" | "apple";

export type NativeOAuthOutcome =
  | { status: "success" }
  | { status: "canceled" }
  | { status: "error"; code: "network" | "provider_disabled" | "unknown" };

/** True when social sign-in must use the deep-link flow above. */
export function needsNativeOAuth(): boolean {
  return nativePlatform() !== null;
}

function log(event: string, detail?: Record<string, string | boolean>) {
  // Never log tokens, codes or emails.
  console.info(`[oauth-native] ${event}`, detail ?? {});
}

function classify(err: unknown): "network" | "provider_disabled" | "unknown" {
  const message = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (message.includes("provider") && (message.includes("not") || message.includes("disabled")))
    return "provider_disabled";
  if (message.includes("network") || message.includes("fetch") || message.includes("offline")) return "network";
  return "unknown";
}

async function openExternal(url: string) {
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  } catch {
    window.open(url, "_system");
  }
}

async function closeExternal() {
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    /* nothing to close */
  }
}

/**
 * Resolves with the `code` (or an implicit-flow hash) from the deep link.
 *
 * Also settles early when the user backs out of the provider screen: Capacitor
 * Browser fires `browserFinished` when the sheet is dismissed, and the app
 * `resume` event covers wrappers/versions that don't. Both give the deep link a
 * short grace period first, because a successful redirect also closes the sheet.
 */
async function waitForDeepLink(timeoutMs = 180_000): Promise<URL | null> {
  const { App } = await import("@capacitor/app");
  const { Browser } = await import("@capacitor/browser");
  return new Promise<URL | null>((resolve) => {
    let settled = false;
    const removers: Array<() => void> = [];
    let graceTimer: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: URL | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (graceTimer) clearTimeout(graceTimer);
      for (const remove of removers) {
        try {
          remove();
        } catch {
          /* listener already gone */
        }
      }
      resolve(value);
    };

    /** Give a redirect in flight a moment before treating dismissal as cancel. */
    const cancelAfterGrace = () => {
      if (settled || graceTimer) return;
      graceTimer = setTimeout(() => finish(null), 1_200);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    const register = (handle: Promise<{ remove: () => void }>) => {
      removers.push(() => void handle.then((h) => h.remove()).catch(() => undefined));
    };

    register(
      App.addListener("appUrlOpen", ({ url }) => {
        if (!url || !url.startsWith(NATIVE_OAUTH_SCHEME)) return;
        try {
          finish(new URL(url));
        } catch {
          finish(null);
        }
      }),
    );

    register(
      Browser.addListener("browserFinished", () => {
        log("browser_dismissed");
        cancelAfterGrace();
      }),
    );

    register(
      App.addListener("resume", () => {
        cancelAfterGrace();
      }),
    );
  });
}


export async function signInWithProviderNative(provider: NativeOAuthProvider): Promise<NativeOAuthOutcome> {
  try {
    log("authorize_started", { provider });
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${OAUTH_SITE_URL}/auth/callback?native=1`,
        skipBrowserRedirect: true,
      },
    });
    if (error || !data?.url) {
      const code = classify(error);
      log("authorize_url_failed", { provider, code });
      return { status: "error", code };
    }

    const linkPromise = waitForDeepLink();
    await openExternal(data.url);
    const deepLink = await linkPromise;
    await closeExternal();

    if (!deepLink) {
      log("authorize_canceled", { provider });
      return { status: "canceled" };
    }

    const code = deepLink.searchParams.get("code");
    if (code) {
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) {
        const kind = classify(exchangeError);
        log("exchange_failed", { provider, code: kind });
        return { status: "error", code: kind };
      }
      log("session_established", { provider });
      return { status: "success" };
    }

    // Implicit fallback: tokens arrive in the fragment.
    const hash = new URLSearchParams(deepLink.hash.replace(/^#/, ""));
    const access_token = hash.get("access_token");
    const refresh_token = hash.get("refresh_token");
    if (access_token && refresh_token) {
      const { error: sessionError } = await supabase.auth.setSession({ access_token, refresh_token });
      if (sessionError) return { status: "error", code: classify(sessionError) };
      log("session_established", { provider, implicit: true });
      return { status: "success" };
    }

    if (deepLink.searchParams.get("error")) {
      log("provider_error", { provider });
      return { status: "error", code: "unknown" };
    }
    return { status: "canceled" };
  } catch (err) {
    const code = classify(err);
    log("authorize_failed", { provider, code });
    return { status: "error", code };
  }
}
